import React from 'react';
import FileBadge from './FileBadge';

/**
 * Reader island for non-managers (payload mode 'read' or 'teaser' — see
 * ForumController::eventArticlePayload). View and download are gated
 * INDEPENDENTLY: each block shows the same locked overlay when its own rule
 * denies access.
 * - content: locked (blurred placeholder + overlay) when the visitor can't view;
 * - attachments: locked (blurred list + overlay) when they can't download.
 * The real body is never sent to a visitor who can't view it.
 */

/* Locked-state overlay: lock icon + context (why it's gated, by the block's own
   rule) + action. Guests get inline log-in/register links; a logged-in
   non-member gets "Request to join" (chapter membership always needs approval).
   Exception: registrants_only never offers "Request to join" (membership alone
   doesn't unlock it) — the page's own CTA/banners carry the right action per
   status and timing, so the overlay stays message-only. */
function GateOverlay({ rule, subject, verb, chapterPhrase, loggedIn, joinUrl }) {
    const memberGated = rule === 'members_only' || rule === 'members_and_registrants';
    const attendeeGated = rule === 'registrants_only';
    const context = rule === 'members_only'
        ? `${subject} available only to active ${chapterPhrase} members.`
        : rule === 'members_and_registrants'
            ? `${subject} available only to active ${chapterPhrase} members and approved event registrants.`
            : attendeeGated
                ? `${subject} available only to approved event attendees.`
                : `${subject} available only to signed-in users.`;

    return (
        <div className="ea-locked__overlay">
            <div className="ea-locked__panel">
                <span className="ea-locked__icon"><i className="fa-solid fa-lock" /></span>
                <p className="ea-locked__title">{context}</p>
                {loggedIn ? (
                    attendeeGated ? null : (
                        <a className="sp-ocard-btn sp-ocard-btn-ghost" href={joinUrl}>Request to join</a>
                    )
                ) : (
                    // Disabled for now: "register" reused joinUrl regardless of ctaMode
                    // (dead end once the event ends; wrong link type vs the on-page
                    // modal). Rebuild once ctaMode reaches this payload.
                    // <p className="ea-locked__msg">
                    //     <a className="ea-link" role="button" data-lk-modal="lkModalLogin">Log in</a>
                    //     {memberGated ? ' to request membership, or ' : ` to ${verb}, or `}
                    //     <a className="ea-link" href={joinUrl}>register</a>
                    //     {' if you don\'t have an account yet.'}
                    // </p>
                    null
                )}
            </div>
        </div>
    );
}

export default function EventArticleReader({ payload }) {
    const {
        article, canDownload, chapterName, joinUrl, csrf, urls, mode, viewRule, downloadRule, loggedIn,
    } = payload;
    const files = (article && article.files) || [];
    const isTeaser = mode === 'teaser'; // can't view the content

    const chapterPhrase = chapterName ? `${chapterName} chapter` : 'the chapter';

    const showAttachments = files.length > 0;

    // Same rule as the content gate + both locked ⇒ the attachments block still
    // shows (blurred file names), but WITHOUT repeating the message/CTA the
    // content gate already carries.
    const dedupeDownloadMessage = isTeaser && viewRule === downloadRule;

    /* Form POST so the attachment downloads in place (no navigation). */
    const downloadFile = (filename) => {
        const form = document.createElement('form');
        form.method = 'POST';
        form.action = urls.download;
        form.style.display = 'none';
        [['_token', csrf], ['filename', filename]].forEach(([name, value]) => {
            const input = document.createElement('input');
            input.type = 'hidden';
            input.name = name;
            input.value = value;
            form.appendChild(input);
        });
        document.body.appendChild(form);
        form.submit();
        form.remove();
    };

    return (
        <section className={`lk-section ea-section${showAttachments ? '' : ' ea-section--solo'}`}>
            <h2 className="ea-section-title">Event Material</h2>

            <div className="event-grid">
                {/* ── Content block: locked when the visitor can't view ── */}
                <div className="event-grid__main">
                    <div className={`ea-card${isTeaser ? ' ea-card--locked' : ''}`}>
                        {isTeaser ? (
                            /* Blurred placeholder (the real body is never sent) + gate overlay. */
                            <div className="ea-locked">
                                <div className="ea-locked__blur ea-read ck-content" aria-hidden="true">
                                    <h3>Session Recap &amp; Key Notes</h3>
                                    <p>Session recaps in the Legal Operations &amp; KPIs Forum capture the
                                       practical side of running a modern legal function — defining the metrics
                                       that matter, building KPI dashboards, streamlining workflows, and
                                       benchmarking against peers. Each one gathers the main points from the
                                       discussion and the follow-ups that came out of it.</p>
                                    <p className="ea-locked__sub">Key Takeaways</p>
                                    <ul>
                                        <li>Which metrics best reflect a legal team&apos;s performance and value.</li>
                                        <li>How to turn operational data into clearer priorities and decisions.</li>
                                        <li>Common pitfalls when scaling processes — and how to avoid them.</li>
                                    </ul>
                                    <p className="ea-locked__sub">Highlights from the Discussion</p>
                                    <ul>
                                        <li>Approaches that worked for teams at different maturity stages.</li>
                                        <li>Tools and frameworks referenced throughout the session.</li>
                                        <li>Open questions carried forward into upcoming sessions.</li>
                                    </ul>
                                </div>
                                <GateOverlay
                                    rule={viewRule}
                                    subject="This material is"
                                    verb="view it"
                                    chapterPhrase={chapterPhrase}
                                    loggedIn={loggedIn}
                                    joinUrl={joinUrl}
                                />
                            </div>
                        ) : article.body ? (
                            <div className="ea-read ck-content" dangerouslySetInnerHTML={{ __html: article.body }} />
                        ) : (
                            <p className="ea-read-empty">No content has been added yet.</p>
                        )}
                    </div>
                </div>

                {/* ── Attachments block: locked when the visitor can't download,
                       independently of whether the content is viewable. ── */}
                {showAttachments && (
                    <aside className="event-grid__aside">
                        {/* Locked WITH its own message = the full overlay treatment. Locked but
                            duplicating the content gate (same rule) or unlocked = a plain list —
                            only the file name is blurred, so the icon stays crisp. */}
                        {!canDownload && !dedupeDownloadMessage ? (
                            <div className="ea-card ea-card--locked">
                                <h4 className="ea-block-title ea-block-title--primary ea-locked__heading">Attachments</h4>
                                <div className="ea-locked">
                                    <div className="ea-locked__files" aria-hidden="true">
                                        <ul className="ea-file-list">
                                            {files.map((file) => (
                                                <li key={file.filename} className="ea-file-row">
                                                    <FileBadge filename={file.filename} />
                                                    <span className="ea-file-main">
                                                        <span className="ea-file-name ea-file-name--locked">{file.filename}</span>
                                                    </span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                    <GateOverlay
                                        rule={downloadRule}
                                        subject="These files are"
                                        verb="download them"
                                        chapterPhrase={chapterPhrase}
                                        loggedIn={loggedIn}
                                        joinUrl={joinUrl}
                                    />
                                </div>
                            </div>
                        ) : (
                            <div className="ea-card">
                                <h4 className="ea-block-title ea-block-title--primary">Attachments</h4>
                                <ul className="ea-file-list">
                                    {files.map((file) => (
                                        <li key={file.filename} className="ea-file-row">
                                            <FileBadge filename={file.filename} />
                                            <span className="ea-file-main">
                                                <span className={`ea-file-name${!canDownload ? ' ea-file-name--locked' : ''}`}>{file.filename}</span>
                                            </span>
                                            {canDownload && (
                                                <button
                                                    type="button"
                                                    className="back-btn ea-dl-btn"
                                                    onClick={() => downloadFile(file.filename)}
                                                >
                                                    ⬇ Download
                                                </button>
                                            )}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </aside>
                )}
            </div>
        </section>
    );
}
